import { useMutation } from "@tanstack/react-query";
import { api } from "../api";
import {
  runSeasonCopy,
  type CopyPlan,
} from "../../pages/settings/SeasonAndEvents/copySeasons";

interface CopySeasonsVariables {
  plans: CopyPlan[];
  overwrite: boolean;
}

interface CopySeasonsResult {
  copied: number;
  overwritten: number;
  failures: string[];
  overridesSkipped: string[];
}

/**
 * Mutation that copies a prepared batch of seasons into the following year.
 *
 * The orchestration (delete conflicts → create → apply per-room overrides, with
 * per-season failure isolation) lives in the pure `runSeasonCopy` helper so it
 * stays unit-testable; this hook is the TanStack Query wrapper that exposes
 * `isLoading` and standardizes error surfacing per the custom-hook pattern.
 *
 * Note: seasons are not stored in a TanStack query — they live in
 * SettingsContext and are refreshed imperatively by the caller's `onUpdate`
 * (→ updateData), so there is no query key to invalidate here. The caller is
 * responsible for resyncing on settle and for success/partial-failure toasts,
 * since those need view context (target year, which seasons failed).
 */
export function useCopySeasons(userId?: string) {
  return useMutation<CopySeasonsResult, Error, CopySeasonsVariables>({
    mutationFn: ({ plans, overwrite }) => {
      if (!userId) throw new Error("User ID is required");
      if (plans.length === 0) throw new Error("No seasons to copy");
      return runSeasonCopy({ userId, plans, overwrite, api });
    },
  });
}
